import {
  Iterator,
  IteratorMap,
  iteratorValue,
  iteratorDone,
  iteratorGet
} from './imiter.js'

import {
  hash,
  hashMerge,
  hashMurmurHashOfSize
} from './imhash.js'

import {
  NOT_SET,
  IS_MAP_SYMBOL,
  SHAPE_MAP,
  ITERATE_VALUES,
  ITERATE_ENTRIES
} from './imconst.js'

import {
  OwnerID,
  MakeRef
} from './imtrie.js'

import {
  probeIsMap,
  probeIsIterator,
  probeIsSame
} from './improbe.js'

import {
  utilAssertTuple,
  utilAssertNotInfinite
} from './imutil.js'

import {
  imerrInvalidMapInitialObjValue,
  imerrUpdateKeyNotFound
} from './imerr.js'

import {
  kernelKeyedGet,
  kernelKeyedIterate,
  kernelKeyedArrayMapCreate,
  kernelKeyedOpUpdateOrCreate
} from './imkernelKeyed.js'

const collectionSeqOpIterateUncached = (cx, fn) => {
  const collection = cx._collection
  const iterator = iteratorGet(collection)
  let iterations = 0
  if (probeIsIterator(iterator)) {
    let step
    while (!(step = iterator.next()).done) {
      if (fn(step.value, iterations++, cx) === false) {
        break
      }
    }
  }
  return iterations
}

const collectionSeqOpIteratorUncached = (cx, type) => {
  const collection = cx._collection
  const iterator = iteratorGet(collection)
  if (!probeIsIterator(iterator)) {
    return new Iterator(iteratorDone)
  }
  let iterations = 0
  return new Iterator(() => {
    const step = iterator.next()
    return step.done ? step : iteratorValue(type, iterations++, step.value)
  })
}

const keyedCollectionCreate = o =>
  probeIsMap(o) ? o : imMapFromObj(o)

const imOpMergeIntoKeyedWith = (
  cx,
  collections
) => {
  const iters = []
  for (let ii = 0; ii < collections.length; ii++) {
    const collection = keyedCollectionCreate(collections[ii])
    if (collection.size !== 0) {
      iters.push(collection)
    }
  }
  if (iters.length === 0) {
    return cx
  }
  if (cx.size === 0 && !cx.__ownerID && iters.length === 1) {
    return iters[0]
    // return cx.create(iters[0])
  }

  return imOpWithMutations(cx, cxmutable => {
    for (let ii = 0; ii < iters.length; ii++) {
      imOpForEach(iters[ii], (value, key) => imOpSet(cxmutable, key, value))
      // iters[ii].forEach((value, key) => imOpSet(cxmutable, key, value))
    }
  })
}

const imOpMerge = (cx, iters) => {
  return imOpMergeIntoKeyedWith(
    cx,
    Array.isArray(iters) ? iters : [iters],
    undefined
  )
}

const imOpHas = (map, key) => (
  imOpGet(map, key, NOT_SET) !== NOT_SET)

const imOpMapKeys = (cx, mapper, context) => {
  return imOpWithMutations(cx, cxmutable => {
    imOpForEach(cxmutable, (v, k) => {
      const kresolved = mapper.call(context, k, v, cxmutable)
      if (kresolved !== k) {
        imOpRemove(cxmutable, k)
        imOpSet(cxmutable, kresolved, v)
        // cxmutable.delete(k)
        // cxmutable.set(kresolved, v)
      }
      return v
    })
  })
}


const imOpFilter = (cx, predicate) => {
  return Object.assign({}, {
    // [IS_SEQ_SYMBOL]: true,
    // [IS_KEYED_SYMBOL]: true,
    // [IS_COLLECTION_SYMBOL]: true,
    collection: cx,
    filterUseKeys: true,
    filterPredicate: predicate
  })
}

// Returns a new map Map2 with the keys K1 through Kn
// and their associated values from map Map1.
// Any key in Ks that does not exist in Map1 is ignored.
const imOpWith = (map, keys) => !keys.length
  ? imMapFromEmpty()
  : imOpWithMutations(imMapFromEmpty(), mapWith => {
    // const iter = SeqKeyed(value)

    // utilAssertNotInfinite(iter.size)
    keys.reduce((mapWith, key) => {
      // if (imOpGet(map, key, NOT_SET) !== NOT_SET) {
      if (imOpHas(map, key)) {
        imOpSet(mapWith, key, imOpGet(map, key))
      }

      return  mapWith
    }, mapWith)
    // imOpForEach(iter, (v, k) => mapUpdate(map, k, v))
  })

const imOpForEach = (cx, sideEffect, context) => {
  utilAssertNotInfinite(cx.size)

  return imOpIterate(
    cx, context ? sideEffect.bind(context) : sideEffect)
}

// like 'set' or 'put' but error if key not found
const imOpUpdate = (map, key, value) => {
  if (!imOpHas(map, key)) {
    throw imerrUpdateKeyNotFound(key)
  }

  return imOpSet(map, key, value)
}

// Update a value in a Map1 associated with Key by calling Fun
// on the old value to get a new value. An exception {badkey,Key}
// is generated if Key is not present in the map.
const imOpUpdateWith = (map, key, fn, init) => {
  if (!imOpHas(map, key)) {
    if (typeof init === 'undefined')
      throw imerrUpdateKeyNotFound(key)

    return imOpSet(map, key, fn(init))
  }

  return imOpSet(map, key, fn(imOpGet(map, key)))
}

const imMapFromNativeMap = plainMap => (
  imOpWithMutations(imMapFromEmpty(), cxmutable => {
    plainMap.forEach((v, k) => {
      imOpSet(cxmutable, k, v)
    })
  }))


const imOpFindEntry = (cx, predicate, context, notSetValue) => {
  let found = notSetValue
  imOpIterate(cx, (v, k, c) => {
    if (predicate.call(context, v, k, c)) {
      found = [k, v]
      return false
    }
  })
  return found
}

const imOpFind = (cx, predicate, context, notSetValue) => {
  const entry = imOpFindEntry(cx, predicate, context)
  return entry ? entry[1] : notSetValue
}

const collectionReduce = (
  cx,
  reducer,
  reduction,
  context,
  useFirst,
  reverse
) => {
  utilAssertNotInfinite(cx.size)
  imOpIterate(cx, (v, k, c) => {
    if (useFirst) {
      useFirst = false
      reduction = v
    } else {
      reduction = reducer.call(context, reduction, v, k, c)
    }
  }, reverse)
  return reduction
}

const imOpReduce = (cx, reducer, initialReduction, context) => {
  return collectionReduce(
    cx,
    reducer,
    initialReduction,
    context,
    typeof initialReduction === 'undefined' && typeof context === 'undefined',
    false
  )
}

const imOpWasAltered = cx => {
  return cx.__altered
}

const imOpAsMutable = cx => {
  return cx.__ownerID ? cx : imOpEnsureOwner(cx, new OwnerID())
}

const imOpEquals = (a, b) => {
  if (a === b) {
    return true
  }

  if (
    !probeIsMap(b) ||
    // @ts-expect-error size should exists on Collection
    (a.size !== undefined && b.size !== undefined && a.size !== b.size) ||
    // @ts-expect-error __hash exists on Collection
    (a.__hash !== undefined &&
      // @ts-expect-error __hash exists on Collection
      b.__hash !== undefined &&
      // @ts-expect-error __hash exists on Collection
      a.__hash !== b.__hash) ||
    probeIsMap(a) !== probeIsMap(b)
  ) {
    return false
  }

  // @ts-expect-error size should exists on Collection
  if (a.size === 0 && b.size === 0) {
    return true
  }

  const notAssociative = !probeIsMap(a)

  let flipped = false, allEqual = true

  if (a.size === undefined) {
    // @ts-expect-error size should exists on Collection
    if (b.size === undefined) {
      if (typeof a.cacheResult === 'function') {
        a.cacheResult()
      }
    } else {
      flipped = true
      const _ = a
      a = b
      b = _
    }
  }

  const bSize =
    imOpIterate(b, (v, k) => {
      if (
        notAssociative
          ? !imOpHas(a, v)
          : flipped
            ? !probeIsSame(v, imOpGet(a, k, NOT_SET))
            : !probeIsSame(imOpGet(a, k, NOT_SET), v)
      ) {
        allEqual = false
        return false
      }
    })

  return (
    allEqual &&
    // @ts-expect-error size should exists on Collection
    a.size === bSize
  )
}

const imOpHashCodeCreate = cx => {
  if (cx.size === Infinity) {
    return 0
  }

  // let h = probeIsOrdered(cx) ? 1 : 0
  let h = 0
  imOpIterate(cx,
    (v, k) => h = (31 * h + hashMerge(hash(v), hash(k))) | 0)

  return hashMurmurHashOfSize(cx.size, h)
}

const imOpHashCode = cx => {
  // Drop any high bits from accidentally long hash codes.
  // return smi(o.hashCode(o))
  return cx.__hash || (cx.__hash = imOpHashCodeCreate(cx))
}

const imOpWithMutations = (cx, fn) => {
  const mutable = imOpAsMutable(cx)
  fn(mutable)
  return imOpWasAltered(mutable)
    ? imOpEnsureOwner(mutable, cx.__ownerID) : cx
}

const imMapFromList = list => {
  utilAssertTuple(Array.isArray(list) ? list[0] : list)

  return imOpWithMutations(imMapFromEmpty(), map => {
    list.reduce((acc, tuple) => (
      imOpSet(acc, tuple[0], tuple[1])
    ), map)
  })
}

const imOpMap = (m, mapper, context) => {
  return imOpWithMutations(m, map => {
    imOpForEach(map, (value, key) => {
      // map.forEach((value, key) => {
      // map.set(key, mapper.call(context, value, key, m))
      imOpSet(map, key, mapper.call(context, value, key, m))
    })
  })
}

const imOpRemove = (m, k) => imOpSet(m, k, NOT_SET)

const imOpWithout = (m, keys) => {
  return keys.size === 0 ? m : imOpWithMutations(m, map => keys
    .reduce((acc, key) => imOpRemove(acc, key), map))
}

// name indicates a 'listing' generating behaviour
// vs more passing sounding 'keys'
const imOpToKeys = cx => {
  let keys = []
  imOpMap(cx, (v, k) => keys.push(k))
  return keys
}

const imOpToValues = cx => {
  let values = []
  imOpMap(cx, v => values.push(v))
  return values
}

const imOpToObject = cx => {
  utilAssertNotInfinite(cx.size)
  const object = {}
  imOpIterate(cx, (v, k) => {
    object[k] = v
  })
  return object
}

const imOpValues = cx =>
  imOpIterator(cx, ITERATE_VALUES)
const imOpEntries = cx =>
  imOpIterator(cx, ITERATE_ENTRIES)

const imOpGet = (m, k, notSetValue) => m._root
  ? kernelKeyedGet(m._root, 0, undefined, k, notSetValue)
  : notSetValue

const imOpToList = cx => {
  return cx._root.entries
}

const imOpNext = cxiter => {
  const n = cxiter.next()
  // {K2, V2, I2} = maps:next(I1), {K2, V2}.
  return [n.value[0], n.value[1], cxiter]
  // return collection.__iterator(ITERATE_ENTRIES)
}

const imOpMapIterate = (m, fn, reverse) => {
  let iterations = 0

  if (m._root) {
    kernelKeyedIterate(m._root, entry => {
      iterations++
      return fn(entry[1], entry[0], m)
    }, reverse)
  }

  return iterations
}

const iterateUncachedFilter = (cx, fn, reverse) => {
  let iterations = 0

  const prefilter = cx.prefilter || cx
  const collection = prefilter.collection
  const useKeys = prefilter.filterUseKeys
  const predicate = prefilter.filterPredicate
  // imOpIteratorUncachedFilter(cx, )
  // imOpIterate(cx, (v, k, c) => {
  imOpIterate(collection, (v, k, c) => {
    if (predicate(v, k, c)) {
      iterations++
      return fn(v, useKeys ? k : iterations - 1, this)
    }
  }, reverse)
  return iterations
}

const collectionSeqOpIterate = (seq, fn) => {
  const cache = seq._cache
  if (cache) {
    const size = cache.length
    let i = 0
    while (i !== size) {
      const entry = cache[i++]
      if (fn(entry[1], entry[0], seq) === false) {
        break
      }
    }
    return i
  }

  // return seq.__iterateUncached(fn)
  return (seq.prefilter || seq.filterPredicate)
    ? iterateUncachedFilter(seq, fn)
    : collectionSeqOpIterateUncached(seq, fn)
}

const imOpIterate = (cx, fn, reverse) => {
  return probeIsMap(cx)
    ? imOpMapIterate(cx, fn, reverse)
    : collectionSeqOpIterate(cx, fn, reverse)
}

const imOpSize = cx =>
  typeof cx.size === 'number'
    ? cx.size : cx._values.size

const imOpEnsureOwner = (m, ownerID) => {
  if (ownerID === m.__ownerID) {
    return m
  }
  if (!ownerID) {
    // remove ownershipe
    if (m.size === 0) {
      return imMapFromEmpty()
    }
    m.__ownerID = ownerID
    m.__altered = false
    return m
  }
  return imMapCreate(m.size, m._root, ownerID, m.__hash)
}

const collectionSeqOpIterator = (seq, type) => {
  const cache = seq._cache
  if (cache) {
    const size = cache.length
    let i = 0
    return new Iterator(() => {
      if (i === size) {
        return iteratorDone()
      }
      const entry = cache[i++]
      return iteratorValue(type, entry[0], entry[1])
    })
  }
  return collectionSeqOpIteratorUncached(seq, type)
  // return seq.__iteratorUncached(type)
}

const collectionMapOpIterator = (m, type, reverse) => {
  return new IteratorMap(m, type, reverse)
}

const imOpIterator = (cx, type) => {
  return probeIsMap(cx)
    ? collectionMapOpIterator(cx, type)
    : collectionSeqOpIterator(cx, type)
}

const imMapPropertiesCreate = (cache => () => cache || (cache = ({
  [IS_MAP_SYMBOL]: true,
  get (k, nsv) { return imOpGet(this, k, nsv) },
  set (k, v) { return imOpSet(this, k, v) },
  has (k) { return imOpHas(this, k) },
  rm (k) { return imOpRemove(this, k) }
})))()

const imMapCreate = (size = 0, root, ownerID, hash) => {
  const map = Object.create(imMapPropertiesCreate())

  // map[ITERATOR_SYMBOL] = map.entries
  map.size = size
  map._root = root
  map.__ownerID = ownerID
  map.__hash = hash
  map.__altered = false
  map.__shape = SHAPE_MAP

  return map
}

const imOpSet = (map, k, v) => {
  let newRoot, newSize

  if (!map._root) {
    if (v === NOT_SET) {
      return map
    }
    newSize = 1
    newRoot = kernelKeyedArrayMapCreate(map.__ownerID, [[k, v]])
  } else {
    const didChangeSize = MakeRef()
    const didAlter = MakeRef()
    newRoot = kernelKeyedOpUpdateOrCreate(
      map._root,
      map.__ownerID,
      0,
      undefined,
      k,
      v,
      didChangeSize,
      didAlter
    )
    if (!didAlter.value) {
      return map
    }
    newSize = map.size + (didChangeSize.value ? (v === NOT_SET ? -1 : 1) : 0)
  }
  if (map.__ownerID) {
    map.size = newSize
    map._root = newRoot
    map.__hash = undefined
    map.__altered = true
    return map
  }
  return newRoot
    ? imMapCreate(newSize, newRoot)
    : imMapFromEmpty()
}

const imMapFromEmpty = (
  mapEmptyCached => () =>
    mapEmptyCached || (mapEmptyCached = imMapCreate(0))
)()

const imMapFromObj = (value, valuetype = typeof value) => {
  if (valuetype === 'undefined')
    return imMapFromEmpty()
  if (valuetype !== 'object')
    throw imerrInvalidMapInitialObjValue(value)
  if (probeIsMap(value))
    return value

  const keys = Object.keys(value)
  return !keys.length
    ? imMapFromEmpty()
    : imOpEnsureOwner(keys
      .reduce((map, k) => imOpSet(map, k, value[k]),
        imMapCreate(0, undefined, new OwnerID())))
}

const imMapFromAny = value =>
  value === undefined || value === null
    ? imMapFromEmpty()
    : probeIsMap(value)
      ? value
      : imMapFromObj(value)

export {
  imOpMerge,
  imOpFilter,
  imOpToObject,
  imOpToList,
  imOpNext,
  imOpValues,
  imOpEntries,
  imOpHashCode,
  imOpEquals,
  imOpFindEntry,
  imOpFind,
  imOpForEach,
  imOpReduce,
  imOpSize,
  imOpIterate,
  imOpIterator,
  imOpToKeys,
  imOpToValues,
  imOpAsMutable,
  imOpGet,
  imOpSet,
  imOpHas,
  imOpWith,
  imOpWithout,
  imOpUpdate,
  imOpUpdateWith,
  imOpWithMutations,
  imOpRemove,
  imOpMap,
  imOpMapKeys,

  probeIsMap as imOpIsMap,
  probeIsSame as imOpIsSame,

  imMapPropertiesCreate,
  imMapCreate,
  imMapFromEmpty,
  imMapFromObj,
  imMapFromObj as imMap,
  imMapFromObj as default,
  imMapFromList,
  imMapFromNativeMap,
  imMapFromAny
}
